import {Link} from 'react-router-dom'
import withPopup from "components/utils/withPopup";
import SellwisorLogo from "assets/logos/Sellwisor";
import React, {useEffect, useCallback, useMemo} from "react";
import {useLocalStorage} from "components/App/LocalStorageContext";
import {Icon, List, Label, Button, ListItem, ListHeader, ListContent} from "semantic-ui-react";

import './styles/RequestSegments.scss'
import {requestManager} from "../../requestManager";
import {RL_SELECTED_KEY, REQUEST_CREATED_KEY} from "../utils";

export const RequisitionListSegment = ({canRLEdit, request}) => {
  const [selectedItems, setSelectedItems] = useLocalStorage(RL_SELECTED_KEY, []);
  const [, setCreatedRequest] = useLocalStorage(REQUEST_CREATED_KEY, null);

  const removeItem = useCallback((e, {itemKey}) => {
    const onSuccess = () => {
      const newSelectedItems = selectedItems.filter(({id}) => itemKey !== id);
      setSelectedItems(newSelectedItems);
      // Clear createdRequest only if we just removed the last RL
      if (newSelectedItems.length === 0 && selectedItems.length > 0) {
        setCreatedRequest(null);
      }
    };
    requestManager().removeRLFromRequest(request, itemKey, onSuccess);
  }, [request, selectedItems, setSelectedItems, setCreatedRequest]);

  // Note: Removed the useEffect that was causing race condition
  // Now we only clear createdRequest when explicitly removing the last RL

  const orderedItems = useMemo(
    () => [...selectedItems].sort((a, b) => {
      const aNum = a.attributes?.full_number?.split(/\D/).map(q => parseInt(q) || 0) || [0];
      const bNum = b.attributes?.full_number?.split(/\D/).map(q => parseInt(q) || 0) || [0];
      
      for (let i = 0; i < Math.max(aNum.length, bNum.length); i++) {
        const aVal = aNum[i] || 0;
        const bVal = bNum[i] || 0;
        if (aVal !== bVal) {
          return aVal - bVal;
        }
      }
      return 0;
    }),
    [selectedItems]
  );

  return (
    <>
      <Label as='a' attached="top">
        Selected Requisition Lists
      </Label>
      <List divided verticalAlign='middle'>
        {orderedItems.map(({id, attributes}) => (
          <ListItem key={`rl-${id}`}>
            <ListContent floated='right'>
              {canRLEdit && <Button size="mini" color="red" basic icon="remove" onClick={removeItem} itemKey={id}/>}
            </ListContent>
            <ListContent>
              <ListHeader>{attributes?.full_title}</ListHeader>
              {`${attributes.vessel_name} / ${attributes.equipment?.name}`}
            </ListContent>
          </ListItem>
        ))}
      </List>
    </>
  )
}

/**
 * FIN-1102: human label for the supplier's usual quoting speed.
 * @param {number|null} hours - average latency in hours
 * @returns {string|null}
 */
export const formatQuoteLatency = (hours) => {
  if (hours === null || hours === undefined) return null;
  if (hours < 48) return `~${Math.round(hours)} h`;
  return `~${(hours / 24).toFixed(1)} d`;
};

export const SupplierSegment = ({canCompanyEdit, canRLEdit, request, suppliers = [], supplierStats = {}, onSuppliersChange}) => {
  const [isRemoving, setIsRemoving] = React.useState(false);

  // SELLWISOR-93: suppliers kept on the request but excluded from sending
  const excludedIds = useMemo(
    () => (request?.attributes?.excluded_supplier_ids || []).map(String),
    [request]
  );

  const removeSupplier = useCallback((e, {itemKey}) => {
    if (!request || !request.id) {
      console.error('Cannot remove supplier: request is not defined');
      return;
    }

    setIsRemoving(true);
    const onSuccess = () => {
      setIsRemoving(false);
      // Notify parent to reload suppliers from server
      if (onSuppliersChange) {
        onSuppliersChange();
      }
    }
    const onError = () => {
      setIsRemoving(false);
    }
    requestManager().removeSupplierFromRequest(request, itemKey, onSuccess, onError);
  }, [request, onSuppliersChange]);

  useEffect(() => {
    const bc = new BroadcastChannel("purchasing_request_changes");
    bc.onmessage = (e) => {
      const {data: { action = null, payload = {} } = {}  } = e;
      if (action !== 'companyFormSubmitted') return;
      const supplierId = payload.id;
      const ids = suppliers.map(({id}) => id);
      if (ids.includes(supplierId) && onSuppliersChange) {
        onSuppliersChange();
      }
    }
    return () => bc.close();
  }, [suppliers, onSuppliersChange]);

  const orderedSuppliers = useMemo(
    () => [...suppliers].sort((a, b) => a.attributes.short_name.localeCompare(b.attributes.short_name)),
  [suppliers]
  );

  return (
    <>
      <Label as='a' attached="top">
        Selected Suppliers
      </Label>
      <List divided verticalAlign='middle'>
        {orderedSuppliers.map(({id, attributes}) => {
          const stats = supplierStats[String(id)] || {};
          const quoteLatency = formatQuoteLatency(stats.avg_quote_latency_hours);
          const isExcluded = excludedIds.includes(String(id));

          return (
            <ListItem key={`supplier-${id}`} className={!attributes.purchasing_email && 'warning'}>
              <ListContent floated='left'>
                {!attributes.purchasing_email && withPopup(
                  <Icon color="red" name="warning sign"/>,
                  () => <p>The supplier does not have a <b>PURCHASING EMAIL</b>. To create a quotation, you need to fill
                    in this field in the <b>COMPANY CARD</b>.</p>
                )}
                {attributes.connected_to_sellwisor &&
                  withPopup(<SellwisorLogo width={14} height={14}/>, "Connected to Sellwisor")
                }
              </ListContent>
              <ListContent floated='right'>
                {canCompanyEdit &&
                  <Button size="mini" color="green" basic icon="edit" as={Link} to={`/companies/${id}/edit?tab=partnership`}
                        target={`/companies/${id}/edit`} rel="noopener noreferrer"/>
                }
                {canRLEdit &&
                  <Button size="mini" color="red" basic icon="remove" onClick={removeSupplier} itemKey={id} disabled={isRemoving} loading={isRemoving}/>
                }
              </ListContent>
              <ListContent>
                <ListHeader>
                  {attributes.short_name}
                  {isExcluded &&
                    withPopup(
                      <Label size="mini" color="orange" style={{marginLeft: '0.5em'}}>Excluded</Label>,
                      'Excluded from this request — the supplier will not receive a quotation'
                    )
                  }
                  {quoteLatency &&
                    withPopup(
                      <Label size="mini" basic style={{marginLeft: '0.5em'}}>
                        <Icon name="clock outline"/>{quoteLatency}
                      </Label>,
                      `Usually quotes in ${quoteLatency} (average over the last 12 months)`
                    )
                  }
                </ListHeader>
                {`${attributes.location} / ${attributes.company_types}`}
              </ListContent>
            </ListItem>
          );
        })}
      </List>
    </>
  )
}
